import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Container,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink,
  CardHeader,
} from "reactstrap";
import CourseService from "../../../services/KangarooServices/CourseService";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Edit3 } from "lucide-react";
import {
  formatDate,
  formatDateTime,
} from "../../../Components/Common/FormatDate";
import "../css/detail.css";
import { imageBaseApi } from "../../../utilis/imageHelper";
import "../css/detail.css";

const CourseDetail = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [activeSubSection, setActiveSubSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchCourseDetail = async (id) => {
    try {
      setLoading(true);
      const response = await CourseService.getDetail(id);
      setData(response);

      // Set initial active section
      if (response?.children?.length > 0) {
        const mainChild =
          response.children.find((c) => c.isMainChild) || response.children[0];
        setActiveSection(mainChild.id);
        if (mainChild.hasSubMenu) {
          setExpandedSections([mainChild.id]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch course detail:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCourseDetail(id);
    }
  }, [id]);

  const handleSectionClick = (sectionId, hasSubMenu) => {
    setActiveSection(sectionId);
    if (!hasSubMenu) {
      setActiveSubSection(null);
    }

    // Toggle expansion for sections with submenus
    if (hasSubMenu) {
      setExpandedSections((prev) =>
        prev.includes(sectionId)
          ? prev.filter((id) => id !== sectionId)
          : [...prev, sectionId],
      );
    }
  };

  const handleSubSectionClick = (subSectionId) => {
    setActiveSubSection(subSectionId);
  };

  const getActiveContent = () => {
    if (!data) return null;

    const activeChild = data.children?.find((c) => c.id === activeSection);
    if (!activeChild) return null;

    if (activeSubSection && activeChild.childrenSubDetails?.length > 0) {
      return activeChild.childrenSubDetails.find(
        (sub) => sub.id === activeSubSection,
      );
    }

    return activeChild;
  };

  const renderContentSection = (content) => {
    if (!content) return null;

    return (
      <div className="content-sections">
        {content.textFields?.map((field, index) => (
          <Card
            key={field.id}
            className="content-block mb-4 border-0 shadow-sm"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <CardBody className="p-4">
              {field.heading && (
                <h2 className="section-heading mb-4">{field.heading}</h2>
              )}

              {field.media && (
                <div className="media-container mb-4">
                  <img
                    src={`${imageBaseApi}/${field.media.url}`}
                    alt={field.heading || "Course content"}
                    className="img-fluid rounded content-image"
                  />
                </div>
              )}

              {field.videoUrl && (
                <div className="ratio ratio-16x9 mb-4 rounded overflow-hidden">
                  <iframe
                    src={field.videoUrl}
                    title={field.heading}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {field.description && (
                <div
                  className="content-description"
                  dangerouslySetInnerHTML={{ __html: field.description }}
                />
              )}
            </CardBody>
          </Card>
        ))}

        {content.description && !content.textFields?.length && (
          <Card className="content-block border-0 shadow-sm">
            <CardBody className="p-4">
              <div
                className="content-description"
                dangerouslySetInnerHTML={{ __html: content.description }}
              />
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  return (
    <Container fluid>
      <div className="page-content course-detail-page">
        <BreadCrumb
          title="Course Detail"
          pageTitle="Courses"
          breadcrumbItems={[{ title: "Back to List", link: "/courses" }]}
        />

        {loading ? (
          <div className="loading-container text-center py-5">
            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }}>
              Loading...
            </Spinner>
            <p className="mt-3 text-muted">Loading course details...</p>
          </div>
        ) : data ? (
          <div className="course-detail-container">
            {/* Hero Banner */}
            <Card className="course-hero border-0 shadow-lg mb-4">
              {data.bannerMedia && (
                <div className="hero-image-wrapper">
                  <img
                    src={`${imageBaseApi}/${data.bannerMedia.url}`}
                    alt={data.name}
                    className="hero-image w-100"
                  />
                  <div className="hero-overlay"></div>
                </div>
              )}
              <CardBody className="hero-content p-5">
                <div className="course-badge mb-3">
                  <Badge color="light" className="course-code px-3 py-2">
                    {data.courseCode}
                  </Badge>
                </div>
                <h1 className="course-title text-white mb-2">{data.name}</h1>
                {data.subName && (
                  <p className="course-subtitle text-white mb-0">
                    {data.subName}
                  </p>
                )}
              </CardBody>
            </Card>

            {/* Main Content Area */}
            <Row className="course-content-wrapper">
              {/* Sidebar Navigation */}
              <Col lg={4} className="mb-4 mb-lg-0">
                <Card className="course-sidebar border-0 shadow-sm sticky-top">
                  <CardHeader className=" text-white border-0">
                    <h5 className="fw-bold text-uppercase">
                      {/* <i className="ri-menu-line me-2"></i> */}
                      Course Navigation
                    </h5>
                  </CardHeader>
                  <CardBody className="">
                    <Nav vertical className="nav-pills flex-column">
                      {data.children?.map((child) => (
                        <div key={child.id} className="nav-item-wrapper mb-2">
                          <NavItem>
                            <NavLink
                              className={`nav-button d-flex align-items-center ${
                                activeSection === child.id ? "active" : ""
                              } ${child.isMainChild ? "fw-bold" : ""}`}
                              onClick={() =>
                                handleSectionClick(child.id, child.hasSubMenu)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <span className="nav-icon me-2">
                                {child.hasSubMenu ? (
                                  expandedSections.includes(child.id) ? (
                                    <i class="ri-arrow-down-s-line"></i>
                                  ) : (
                                    <i class="ri-arrow-left-s-line"></i>
                                  )
                                ) : (
                                  "▸"
                                )}
                              </span>
                              <span className="flex-grow-1">
                                {child.heading}
                              </span>
                            </NavLink>
                          </NavItem>

                          {/* Sub-menu */}
                          {child.hasSubMenu &&
                            expandedSections.includes(child.id) &&
                            child.childrenSubDetails?.length > 0 && (
                              <Nav vertical className="sub-nav-list ps-4 mt-2">
                                {child.childrenSubDetails.map((subChild) => (
                                  <NavItem key={subChild.id}>
                                    <NavLink
                                      className={`sub-nav-button ${
                                        activeSubSection === subChild.id
                                          ? "active"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        handleSubSectionClick(subChild.id)
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      {subChild.heading}
                                    </NavLink>
                                  </NavItem>
                                ))}
                              </Nav>
                            )}
                        </div>
                      ))}
                    </Nav>
                  </CardBody>
                </Card>
              </Col>

              {/* Main Content */}
              <Col lg={8}>
                <Card className="course-main-content ">
                  <CardBody className="">
                    {getActiveContent() ? (
                      <>
                        <div className="content-header  border-bottom">
                          <h1 className="content-title mb-1">
                            {getActiveContent().heading}
                          </h1>
                          {getActiveContent().description &&
                            !getActiveContent().textFields?.length && (
                              <div className="content-intro text-muted">
                                <p className="lead">
                                  {getActiveContent().description.substring(
                                    0,
                                    200,
                                  )}
                                  ...
                                </p>
                              </div>
                            )}
                        </div>

                        {renderContentSection(getActiveContent())}
                      </>
                    ) : (
                      <div className="no-content text-center py-5">
                        <i className="ri-file-list-3-line display-1 text-muted mb-3"></i>
                        <p className="text-muted fs-5">
                          Select a section from the navigation to view content.
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          <Card className="error-container border-0 shadow-sm">
            <CardBody className="text-center py-5">
              <i className="ri-error-warning-line display-1 text-danger mb-3"></i>
              <p className="fs-5 text-muted mb-4">
                Failed to load course details. Please try again.
              </p>
              <button
                onClick={() => navigate("/courses")}
                className="btn btn-primary btn-lg px-4"
              >
                <i className="ri-arrow-left-line me-2"></i>
                Back to Courses
              </button>
            </CardBody>
          </Card>
        )}
      </div>
    </Container>
  );
};

export default CourseDetail;
